import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { OPERATION_AT_TX_CENTER_DURATION } from '@lp/features/RoutePlanner/config';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { Case } from '@/api/apis/case';
import { LogisticsProjectionType, RouteLocationType, RouteProjectionType, RouteTypeEnum } from '@/api/apis/route';
import { RouteResourcesType } from '@/types/route';
import { getTimeZonedDate, getTimeZonedDateString } from '@/utils/helpers';
import { OPERATION_TIME_BY_ORGAN } from '@/utils/route';

type RoutePlannerDataContextValue = {
  caseData: Case;
  logistics: LogisticsProjectionType;
  mainRoute: RouteProjectionType;
  dryRunRoute: RouteProjectionType;

  locations: RouteLocationType[];
  caseLocations: RouteLocationType[];
  getLocationById: (locationId?: string) => RouteLocationType | undefined;

  resources: RouteResourcesType;

  donorHospitalDateTimeString: string | null;
  donorOperationEndDateTimeString: string | null;
  recipientHospitalDateTimeString: string | null;
  recipientOperationEndDateTimeString: string | null;
};

const RoutePlannerDataContext = createContext<RoutePlannerDataContextValue | null>(null);

export const RoutePlannerDataProvider = ({ children }: PropsWithChildren) => {
  const caseData = useCaseDetailsWithReviews();
  const logistics = useLogisticsData();
  const mainRoute = useRouteDetails(RouteTypeEnum.Main);
  const dryRunRoute = useRouteDetails(RouteTypeEnum.DryRun);
  const { list: locations, caseLocations, getLocationById } = useRouteLocations();
  const resources = useRouteResources();

  const {
    organType,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
  } = useBaseCaseDetails();

  const donorHospitalDateTimeString = useMemo(() => {
    return getTimeZonedDateString(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  }, [donorHospitalDate, donorHospitalTime, donorHospitalTimeZone]);

  const donorOperationEndDateTimeString = useMemo(() => {
    const dateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);

    if (!dateTime) return null;

    return dateTime.add({ milliseconds: OPERATION_TIME_BY_ORGAN[organType] }).toString();
  }, [donorHospitalDate, donorHospitalTime, donorHospitalTimeZone, organType]);

  const recipientHospitalDateTimeString = useMemo(() => {
    return getTimeZonedDateString(recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone);
  }, [recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone]);

  const recipientOperationEndDateTimeString = useMemo(() => {
    const dateTime = getTimeZonedDate(recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone);

    if (!dateTime) return null;

    return dateTime.add({ milliseconds: OPERATION_AT_TX_CENTER_DURATION }).toString();
  }, [recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone]);

  const value = useMemo<RoutePlannerDataContextValue>(
    () => ({
      caseData,
      logistics,
      mainRoute,
      dryRunRoute,
      locations,
      caseLocations,
      getLocationById,
      resources,
      donorHospitalDateTimeString,
      donorOperationEndDateTimeString,
      recipientHospitalDateTimeString,
      recipientOperationEndDateTimeString,
    }),
    [
      caseData,
      logistics,
      mainRoute,
      dryRunRoute,
      locations,
      caseLocations,
      getLocationById,
      resources,
      donorHospitalDateTimeString,
      donorOperationEndDateTimeString,
      recipientHospitalDateTimeString,
      recipientOperationEndDateTimeString,
    ],
  );

  return <RoutePlannerDataContext.Provider value={value}>{children}</RoutePlannerDataContext.Provider>;
};

export const useRoutePlannerData = (): RoutePlannerDataContextValue => {
  const context = useContext(RoutePlannerDataContext);

  if (!context) {
    throw new Error('useRoutePlannerData must be used within RoutePlannerDataProvider');
  }

  return context;
};
